import { Lightbulb } from 'lucide-react';
import { RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, ResponsiveContainer, Tooltip } from 'recharts';

interface Dimension {
  category: string;
  score: number | string;
  justification: string;
  highlights: string[];
  improvements: string[];
  coaching: string[];
}

interface RecommendationsProps {
  recommendations: string[];
  dimensions?: Dimension[];
  isDarkMode: boolean;
}

export default function Recommendations({ recommendations, dimensions, isDarkMode }: RecommendationsProps) {
  const chartData = dimensions?.map(dim => ({
    category: dim.category.split(' ')[0] + (dim.category.includes('&') ? '\n& ' + dim.category.split('& ')[1].split(' ')[0] : ''),
    score: typeof dim.score === 'number' ? dim.score : dim.score === 'N/A' ? 0 : parseFloat(dim.score),
    fullCategory: dim.category,
    displayScore: dim.score
  })) || [];

  return (
    <div className={`rounded-xl p-6 shadow-lg ${
      isDarkMode 
        ? 'bg-[#0a1414] border border-[#1a2424]' 
        : 'bg-white'
    }`}>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Action Recommendations Section */}
        <div>
          <h3 className={`text-lg font-semibold ${
            isDarkMode ? 'text-white' : 'text-[#053333]'
          } mb-4 flex items-center`}>
            <Lightbulb className="w-5 h-5 mr-2 text-yellow-400" />
            Action Recommendations
          </h3>
          <div className="space-y-3">
            {recommendations?.map((recommendation, index) => (
              <div key={index} className="flex items-start gap-3">
                <span className="flex-shrink-0 w-6 h-6 bg-[#1a9f6b] text-white text-xs font-bold rounded-full flex items-center justify-center">
                  {index + 1}
                </span>
                <span className={`text-sm leading-relaxed ${
                  isDarkMode ? 'text-gray-300' : 'text-[#373737]'
                }`}>
                  {recommendation}
                </span>
              </div>
            )) || <div className="text-gray-400">No recommendations available</div>}
          </div>
        </div>

        {/* Radar Chart Section */}
        {dimensions && dimensions.length > 0 && (
          <div>
            <h3 className={`text-lg font-semibold ${
              isDarkMode ? 'text-white' : 'text-[#053333]'
            } mb-4 flex items-center`}>
              <svg className="w-5 h-5 mr-2 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
              Performance Overview
            </h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <RadarChart data={chartData}>
                  <PolarGrid stroke={isDarkMode ? '#374151' : '#E5E7EB'} />
                  <PolarAngleAxis 
                    dataKey="category" 
                    tick={{ 
                      fill: isDarkMode ? '#9CA3AF' : '#6B7280',
                      fontSize: 11,
                      textAnchor: 'middle'
                    }}
                    className="text-xs"
                  />
                  <PolarRadiusAxis 
                    angle={90} 
                    domain={[0, 10]}
                    tick={{ 
                      fill: isDarkMode ? '#9CA3AF' : '#6B7280',
                      fontSize: 10
                    }}
                  />
                  <Radar
                    name="Current Performance"
                    dataKey="score"
                    stroke="#1a9f6b"
                    fill="#1a9f6b"
                    fillOpacity={0.3}
                    strokeWidth={2}
                    dot={{ fill: '#1a9f6b', strokeWidth: 2, r: 4 }}
                  />
                  <Tooltip 
                    contentStyle={{
                      backgroundColor: isDarkMode ? '#1F2937' : '#FFFFFF',
                      border: `1px solid ${isDarkMode ? '#374151' : '#E5E7EB'}`,
                      borderRadius: '8px',
                      color: isDarkMode ? '#FFFFFF' : '#111827',
                      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                    }}
                    formatter={(value, _, props) => [
                      props.payload.displayScore === 'N/A' ? 'N/A' : `${value}/10`,
                      props.payload.fullCategory
                    ]}
                    labelStyle={{ color: isDarkMode ? '#FFFFFF' : '#111827' }}
                  />
                </RadarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}